import { useEffect, useMemo, useState } from "react";
import { Box, Container, Grid, Paper, Typography, Button, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Tabs, Tab } from "@mui/material";
import { ArrowBack, FileDownload } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { ExportToCsv } from "export-to-csv";
import { LoaderBoxCircular } from "../Components/LoaderBox";
import { instance } from "../Components/config";
import { DialogAlert } from "../Components/DialogAlert";

// Pivotea el detalle mes a mes (una fila por producto/mes) en: meses (columnas) x productos (filas).
// Si esReportadoDistinto=true, un mes sin UPSE4 (idUpse4 null) se muestra como "-" (no reportado),
// distinto de un mes reportado con ejecutado=0.
function pivotarFilas(filas, esReportadoDistinto) {
  const mesesMap = new Map();
  filas.forEach((f) => mesesMap.set(f.idMes, f.mes));
  const mesesOrdenados = [...mesesMap.entries()].sort((a, b) => a[0] - b[0]);

  const grupos = new Map();
  filas.forEach((f) => {
    if (!grupos.has(f.id)) {
      grupos.set(f.id, {
        id: f.id,
        detalle: f.detalle,
        departamento: f.departamento,
        municipio: f.municipio,
        localidad: f.localidad,
        porMes: {},
        observaciones: [],
      });
    }
    const grupo = grupos.get(f.id);
    const reportado = esReportadoDistinto ? f.idUpse4 !== null : true;
    grupo.porMes[f.idMes] = {
      programado: Number(f.programado),
      ejecutado: reportado ? Number(f.ejecutado) : null,
      reportado,
    };
    if (f.observaciones) grupo.observaciones.push(`${f.mes}: ${f.observaciones}`);
  });

  const productosFinal = [...grupos.values()].map((g) => {
    let totalProgramado = 0;
    let totalEjecutado = 0;
    mesesOrdenados.forEach(([idMes]) => {
      totalProgramado += g.porMes[idMes]?.programado || 0;
      totalEjecutado += g.porMes[idMes]?.ejecutado || 0;
    });
    return {
      ...g,
      totalProgramado,
      totalEjecutado,
      brecha: totalEjecutado - totalProgramado,
      justificacion: g.observaciones.join(" | ") || "-",
    };
  });

  return { meses: mesesOrdenados, productos: productosFinal };
}

function TablaBrecha({ meses, productos, isLoading, mensajeVacio }) {
  if (productos.length === 0) {
    return !isLoading && (
      <Box sx={{ textAlign: 'center', marginTop: 4 }}>
        <p>{mensajeVacio}</p>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell rowSpan={2}><strong>Detalle</strong></TableCell>
            <TableCell rowSpan={2}><strong>Departamento</strong></TableCell>
            <TableCell rowSpan={2}><strong>Municipio</strong></TableCell>
            <TableCell rowSpan={2}><strong>Centro de costo</strong></TableCell>
            <TableCell align="center" colSpan={meses.length + 1} sx={{ borderLeft: '2px solid #ccc' }}><strong>Programado</strong></TableCell>
            <TableCell align="center" colSpan={meses.length + 1} sx={{ borderLeft: '2px solid #ccc' }}><strong>Ejecutado</strong></TableCell>
            <TableCell rowSpan={2} sx={{ borderLeft: '2px solid #ccc' }}><strong>Brecha</strong></TableCell>
            <TableCell rowSpan={2}><strong>Justificación</strong></TableCell>
          </TableRow>
          <TableRow>
            {meses.map(([idMes, mes]) => (
              <TableCell key={`p-${idMes}`} align="center" sx={{ borderLeft: idMes === meses[0][0] ? '2px solid #ccc' : undefined }}>{mes}</TableCell>
            ))}
            <TableCell align="center"><strong>Total</strong></TableCell>
            {meses.map(([idMes, mes]) => (
              <TableCell key={`e-${idMes}`} align="center" sx={{ borderLeft: idMes === meses[0][0] ? '2px solid #ccc' : undefined }}>{mes}</TableCell>
            ))}
            <TableCell align="center"><strong>Total</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {productos.map((p, index) => (
            <TableRow key={index}>
              <TableCell>{p.detalle}</TableCell>
              <TableCell>{p.departamento}</TableCell>
              <TableCell>{p.municipio}</TableCell>
              <TableCell>{p.localidad}</TableCell>
              {meses.map(([idMes]) => (
                <TableCell key={`p-${idMes}`} align="center" sx={{ borderLeft: idMes === meses[0][0] ? '2px solid #ccc' : undefined }}>
                  {p.porMes[idMes]?.programado ?? '-'}
                </TableCell>
              ))}
              <TableCell align="center"><strong>{p.totalProgramado}</strong></TableCell>
              {meses.map(([idMes]) => (
                <TableCell key={`e-${idMes}`} align="center" sx={{ borderLeft: idMes === meses[0][0] ? '2px solid #ccc' : undefined }}>
                  {p.porMes[idMes]?.ejecutado ?? '-'}
                </TableCell>
              ))}
              <TableCell align="center"><strong>{p.totalEjecutado}</strong></TableCell>
              <TableCell sx={{ borderLeft: '2px solid #ccc', color: p.brecha < 0 ? 'error.main' : 'inherit', fontWeight: p.brecha < 0 ? 'bold' : 'normal' }}>
                {p.brecha}
              </TableCell>
              <TableCell>{p.justificacion}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function UPSE4ReporteBrecha() {
  const location = useLocation();
  const navigate = useNavigate();
  const idUpse2 = location.state?.idUpse2;
  const nombreUpse2 = location.state?.nombreUpse2;
  const anio = location.state?.anio;

  const [tab, setTab] = useState(0);
  const [filasMensual, setFilasMensual] = useState([]);
  const [filasAnual, setFilasAnual] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialog, setDialog] = useState({ title: "", text: "" });

  useEffect(() => {
    if (!idUpse2) {
      navigate("/UPSE4");
      return;
    }
    Promise.all([
      instance.get(`/upse4/reporteBrecha/${idUpse2}`),
      instance.get(`/upse4/reporteBrechaAnual/${idUpse2}`),
    ])
      .then(([resMensual, resAnual]) => {
        setFilasMensual(resMensual.data);
        setFilasAnual(resAnual.data);
      })
      .catch((err) => {
        setDialog({ title: "ERROR", text: err.response?.data?.message || "Error desconocido" });
        setOpenDialog(true);
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idUpse2]);

  const mensual = useMemo(() => pivotarFilas(filasMensual, false), [filasMensual]);
  const anual = useMemo(() => pivotarFilas(filasAnual, true), [filasAnual]);

  const activo = tab === 0 ? mensual : anual;
  const nombreReporte = tab === 0 ? "MESES_REPORTADOS" : "ANUAL_COMPLETO";

  const handleExportCsv = () => {
    const { meses, productos } = activo;
    const headers = [
      "Detalle", "Departamento", "Municipio", "Centro de costo",
      ...meses.map(([, mes]) => `Programado ${mes}`), "Programado Total",
      ...meses.map(([, mes]) => `Ejecutado ${mes}`), "Ejecutado Total",
      "Brecha", "Justificación"
    ];

    const rows = productos.map((p) => {
      const fila = {
        Detalle: p.detalle,
        Departamento: p.departamento,
        Municipio: p.municipio,
        "Centro de costo": p.localidad,
      };
      meses.forEach(([idMes, mes]) => { fila[`Programado ${mes}`] = p.porMes[idMes]?.programado ?? 0; });
      fila["Programado Total"] = p.totalProgramado;
      meses.forEach(([idMes, mes]) => { fila[`Ejecutado ${mes}`] = p.porMes[idMes]?.ejecutado ?? ""; });
      fila["Ejecutado Total"] = p.totalEjecutado;
      fila["Brecha"] = p.brecha;
      fila["Justificación"] = p.justificacion === "-" ? "" : p.justificacion;
      return fila;
    });

    const csvExporter = new ExportToCsv({
      filename: `REPORTE_BRECHA_UPSE4_${nombreReporte}_${anio || ''}_${idUpse2}_${new Date().toISOString().slice(0, 10)}`,
      fieldSeparator: ";",
      quoteStrings: '"',
      decimalSeparator: ".",
      showLabels: true,
      useBom: true,
      useKeysAsHeaders: false,
      headers
    });
    csvExporter.generateCsv(rows);
  };

  return (
    <Container>
      <Box sx={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>Volver</Button>
        <h2 style={{ textAlign: 'center', flex: 1, margin: 0 }}>Reporte de brecha</h2>
      </Box>
      <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
        {nombreUpse2} {anio ? `- ${anio}` : ''}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <LoaderBoxCircular isLoading={isLoading}>
          <Container maxWidth="xl" sx={{ marginBottom: 5 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={tab} onChange={(e, value) => setTab(value)}>
                <Tab label="Meses reportados" />
                <Tab label="Año completo" />
              </Tabs>
            </Box>

            <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<FileDownload />}
                onClick={handleExportCsv}
                disabled={activo.productos.length === 0}
              >
                Exportar CSV
              </Button>
            </Grid>

            <TablaBrecha
              meses={activo.meses}
              productos={activo.productos}
              isLoading={isLoading}
              mensajeVacio={tab === 0
                ? "Este UPSE-2 todavía no tiene ningún UPSE-4 reportado."
                : "Este UPSE-2 no tiene productos o cultivares planificados."}
            />
          </Container>
        </LoaderBoxCircular>
      </Box>

      <DialogAlert
        title={dialog.title}
        text={dialog.text}
        open={openDialog}
        setOpen={setOpenDialog}
      />
    </Container>
  );
}

export { UPSE4ReporteBrecha };
